import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

import cloneDeep from 'lodash/cloneDeep';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  Subscription,
  filter,
  tap,
  switchMap,
} from 'rxjs';
import { EditorChangeContent, EditorChangeSelection } from 'ngx-quill';

import {
  PublicWaitlist,
  PublicWaitlistFieldOptions,
  PublicWaitlistFileUploadResponse,
} from '@shared/restaurant/models';
import { StringKeyDictionary } from '@shared/types';
import { Service } from '@shared/service/models';
import { Resource } from '@shared/resource/models';
import { Location } from '@shared/location/models';
import { quillConfig, CommonRadioButtonItem } from '@tablesready/common-ui';
import { DownloadQrCodeService, ImportService } from '../../services';
import { PricePlansRestrictionsService } from '../../../services';
import { TabValues, PublicPagesSections, PublicPagesModes } from './enums';
import {
  CheckinCap,
  MessageObj,
  OptionItem,
  OptionsObject,
  PublicPageTab,
} from './models';
import {
  DEFAULT_PUBLIC_WAITING_LIST_VALUE,
  getAddOptions,
  getFieldsList,
  getViewOption,
  CUSTOM_TEXT_PAGES,
  PUBLIC_PAGES_TABS_DATA,
  PUBLIC_WAITLIST_FIELD_OPTIONS,
  WAITLIST_FIELD_OPTIONS_LABELS_DEFAULT,
  WAITLIST_FIELD_OPTIONS_REQUIRED_FIELDS_DEFAULT,
  WAILIST_IMAGE_DISPLAY_OPTIONS,
  WAITLIST_FIELD_OPTIONS_CUSTOM_TEXT_DEFAULT,
  WAITLIST_FIELD_OPTIONS_DEFAULT,
  WAITLIST_FIELD_SETTING_MAX_TIME_DEFAULT,
  WAITLIST_FIELD_SETTINGS_THEME_DEFAULT,
} from './public-options.constants';
import {
  PricePlansRestrictionsModalService,
  ResourcesService,
  ScheduleService,
  ServicesService,
  WhoService,
} from '../../../services';
import { PublicOptionsService } from './public-options.service';
import { ImageDisplayOption } from './models/image-display';
import { FieldOption } from './models/field-option';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import { AlertsService } from '@tablesready/frontend-alerts-bar';

@Component({
  selector: 'tablesready-public-options',
  templateUrl: './public-options.component.html',
  styleUrls: ['./public-options.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublicOptionsComponent implements OnInit, OnDestroy {
  @ViewChild('takeInput', { static: false }) inputVar: ElementRef;

  editorBackgroundColor: string;
  location: Location;
  schedules: any = [];
  publicWaitFieldsOptions: FieldOption[];
  imageDisplayOptions: ImageDisplayOption[];
  publicWaitlist: PublicWaitlist;
  messagesContainLink;
  publicWaitlistForm: UntypedFormGroup;
  origin: string;
  file: File;
  checkinCapState: CheckinCap;
  publicCheckinUrl = '';
  publicWaitlistUrl = '';
  publicOnlineBookingUrl = '';
  reservationButtonText = 'Make an Appointment';
  tabValues = TabValues;
  customTextPagesSections = PublicPagesSections;
  customTextPagesModes = PublicPagesModes;
  selectedPageTabData: PublicPageTab;
  publicPages: Array<StringKeyDictionary<string>>;
  viewOptions: OptionsObject;
  addOptions: OptionsObject;
  services: Service[];
  resources: Resource[];
  editorConfig = quillConfig;
  customTextPages$: Observable<Array<{name: string; value: string}>>;
  reservationPluralLabel: string;
  reservationLabel: string;
  tabRadioItems: CommonRadioButtonItem[] = [
    {
      name: 'Direct Link',
      value: TabValues.DIRECT_LINK,
    },
    {
      name: 'Embed Page',
      value: TabValues.EMBED_PAGE,
    },
    {
      name: 'Add a Button',
      value: TabValues.ADD_BUTTON,
    },
    {
      name: 'QR Code',
      value: TabValues.QR_CODE,
    },
  ];
  isMobileScreen = false;

  modeRadioItems: CommonRadioButtonItem[] = [];

  manageFieldsList$ = new BehaviorSubject<OptionItem[]>(null);
  private customTextPagesSource$ = new BehaviorSubject<Array<{name: string; value: string}>>(null);
  private isLocationOnBusinessPlan = false;
  private readonly subscriptions = new Subscription();
  private readonly defaultPublicPagesBackgroundColor = '#191c37';

  constructor(
    readonly downloadQrCodeService: DownloadQrCodeService,
    private readonly service: PublicOptionsService,
    private readonly alertsService: AlertsService,
    private readonly whoService: WhoService,
    private readonly importService: ImportService,
    private readonly cdr: ChangeDetectorRef,
    private readonly servicesService: ServicesService,
    private readonly resourcesService: ResourcesService,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly scheduleService: ScheduleService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly pricePlansRestrictionsService: PricePlansRestrictionsService,
    private readonly pricePlansRestrictionsModalService: PricePlansRestrictionsModalService,
  ) {
    this.addOptions = getAddOptions();
    this.viewOptions = getViewOption();
    this.manageFieldsList$.next(getFieldsList());
    this.customTextPages$ = this.customTextPagesSource$.asObservable();

    this.origin = window.location.origin;
  }

  get isImageVisible(): boolean {
    const isImage = String(this.imageControl?.value);
    const position = String(this.positionControl?.value);

    return isImage && (position === 'top' || position === 'bottom');
  }

  get publicPagesDisabled(): boolean {
    return !this.viewControl.value && !this.addControl.value;
  }

  get onlineBookingsEnabled(): boolean {
    return this.location.bookingOptions.onlineReservationsEnabled;
  }

  get isWaitlistAddModeEnabledOnly(): boolean {
    return !this.viewControl.value && this.addControl.value ||
      this.viewControl.value && this.addControl.value;
  }

  get isWaitlistViewModeEnabledOnly(): boolean {
    return this.viewControl.value && !this.addControl.value ||
      this.viewControl.value && this.addControl.value;
  }

  get isServicesEnabled(): boolean {
    return this.location?.serviceSettings.enabled && this.services?.length > 0;
  }

  get isResourcesEnabled(): boolean {
    return this.location?.resourceSettings.enabled && this.resources?.length > 0;
  }

  get isOnlineBookingPageAvailable(): boolean {
    return this.location?.bookingOptions.onlineReservationsEnabled &&
      this.location?.resourceSettings.enabled && this.location?.serviceSettings.enabled;
  }

  get selectedTab(): AbstractControl {
    return this.publicWaitlistForm.get('selectedTab');
  }

  get selectedPublicPageControl(): AbstractControl {
    return this.publicWaitlistForm.get('selectedPublicPage');
  }

  get checkinCapEnabledControl(): AbstractControl {
    return this.publicWaitlistForm.get('checkinCap.enabled');
  }

  get checkinCapControl(): AbstractControl {
    return this.publicWaitlistForm.get('checkinCap');
  }

  get imageControl(): AbstractControl {
    return this.publicWaitlistForm.get('image');
  }

  get viewControl(): AbstractControl {
    return this.publicWaitlistForm.get('view');
  }

  get addControl(): AbstractControl {
    return this.publicWaitlistForm.get('add');
  }

  get positionControl(): AbstractControl {
    return this.publicWaitlistForm.get('position');
  }

  get customMessageEnabledControl(): AbstractControl {
    return this.publicWaitlistForm.get('customMessage.enabled');
  }

  get customMessageControl(): AbstractControl {
    return this.publicWaitlistForm.get('customMessage.message');
  }

  get showUpcomingReservationsControl(): AbstractControl {
    return this.publicWaitlistForm.get('showUpcomingReservations');
  }

  get allowReservationsCheckinControl(): AbstractControl {
    return this.publicWaitlistForm.get('allowReservationsCheckin');
  }

  get fieldLabelsControl(): AbstractControl {
    return this.publicWaitlistForm.get('fieldLabels');
  }

  get fieldLabelsNoteControl(): AbstractControl {
    return this.publicWaitlistForm.get('fieldLabels.note');
  }

  get fieldLabelsMaxSizeControl(): AbstractControl {
    return this.publicWaitlistForm.get('fieldSettings.maxSize');
  }

  ngOnInit(): void {
    const authUserSub = this.authRestaurantService.select()
      .pipe(
        filter(user => user.location !== undefined),
        tap(({ account, location }) => {
          this.publicWaitlist = DEFAULT_PUBLIC_WAITING_LIST_VALUE;
          this.location = location;

          this.isLocationOnBusinessPlan = this.pricePlansRestrictionsService.availableForBusinessPlan(account.subscriptionType, location.recurly);
          this.reservationPluralLabel = this.location.displayOptions?.labels.reservation_plural;
          this.customTextPagesSource$.next(CUSTOM_TEXT_PAGES(this.reservationPluralLabel));

          if (this.location?.publicWaitlist) {
            this.publicWaitlist = cloneDeep(this.location.publicWaitlist);
            this.editorBackgroundColor = this.publicWaitlist.theme.backgroundColor || this.defaultPublicPagesBackgroundColor;
          }

          this.initPublicWaitFieldsOptions();
          this.initDefaultFieldOptions();
          this.initDefaultRequiredFields();
          this.getSchedule();

          if (location.address?.lat && location.address?.lng) {
            this.publicWaitlistForm.get('geofence.enabled').enable({onlySelf: true, emitEvent: false});
          } else {
            this.publicWaitlistForm.get('geofence.enabled').disable({onlySelf: true, emitEvent: false});
          }

          this.checkinCapState = cloneDeep(this.publicWaitlist?.checkinCap);
          this.messagesContainLink = this.checkMessagesContainLink();

          this.loadServicesAndResources();
          this.setupEmbedLinking();

          this.cdr.detectChanges();

          this.reservationLabel = this.location.displayOptions.labels.reservation[0].toUpperCase() +
            this.location.displayOptions.labels.reservation.substring(1).toLowerCase();

          // eslint-disable-next-line max-len
          this.reservationButtonText = `Make ${this.reservationLabel.startsWith('A') ? 'an' : 'a'} ${this.reservationLabel}`;
        }),
      ).subscribe();
    this.subscriptions.add(authUserSub);

    const screenSizeSub = this.breakpointObserver.observe([Breakpoints.Handset, Breakpoints.HandsetLandscape])
      .pipe(tap(({ matches: isMobileScreen }) => this.isMobileScreen = isMobileScreen)).subscribe();
    this.subscriptions.add(screenSizeSub);
  }

  changeCurrentSelectedPage(): void {
    const newPageIndex = String(this.selectedPublicPageControl.value);
    this.selectedPageTabData = this.publicPagesTabsData[newPageIndex];
  }

  get publicPageSection(): PublicPagesSections {
    return this.publicWaitlistForm.get('customText.publicPageSection').value;
  }

  get publicPageMode(): PublicPagesModes {
    return this.publicWaitlistForm.get('customText.publicPageMode').value;
  }

  get isWaitlistViewSelected(): boolean {
    return this.publicPageSection === this.customTextPagesSections.Waitlist && this.publicPageMode === this.customTextPagesModes.View;
  }

  get isWaitlistAddSelected(): boolean {
    return this.publicPageSection === this.customTextPagesSections.Waitlist && this.publicPageMode === this.customTextPagesModes.Add;
  }

  get isBookingsViewSelected(): boolean {
    return this.publicPageSection === this.customTextPagesSections.Bookings && this.publicPageMode === this.customTextPagesModes.View;
  }

  get isBookingsAddSelected(): boolean {
    return this.publicPageSection === this.customTextPagesSections.Bookings && this.publicPageMode === this.customTextPagesModes.Add;
  }

  get isFooterSelected(): boolean {
    return this.publicPageSection === this.customTextPagesSections.Footer;
  }

  get isClosedSelected(): boolean {
    return this.publicPageSection === this.customTextPagesSections.Closed;
  }

  get footerControl(): AbstractControl {
    return this.publicWaitlistForm.get('customText.footer');
  }

  get customTextWaitlistViewControl(): AbstractControl {
    return this.publicWaitlistForm.get('customText.waitlist.view.text');
  }

  get customTextWaitlistAddControl(): AbstractControl {
    return this.publicWaitlistForm.get('customText.waitlist.add.text');
  }

  get customTextBookingsViewControl(): AbstractControl {
    return this.publicWaitlistForm.get('customText.bookings.view.text');
  }

  get customTextBookingsAddControl(): AbstractControl {
    return this.publicWaitlistForm.get('customText.bookings.add.text');
  }

  get geofenceEnabledControl(): AbstractControl {
    return this.publicWaitlistForm.get('geofence.enabled');
  }

  formatTextColor(editorEvent: EditorChangeContent | EditorChangeSelection) {
    if (
      editorEvent.event === 'text-change'&&
      editorEvent.source === 'user' &&
      editorEvent.delta?.ops[1]?.insert
    ) {
      const deltaOps = editorEvent.delta?.ops;

      if (!deltaOps[1]?.attributes?.color) {
        editorEvent.editor.formatText(
          deltaOps[0]?.retain,
          deltaOps[1]?.insert?.length,
          {color: 'rgb(255, 255, 255)'}
        );
      }
    }
  }

  initDefaultRequiredFields(): void {
    if (!this.publicWaitlist.requiredFields) {
      this.publicWaitlist.requiredFields = WAITLIST_FIELD_OPTIONS_REQUIRED_FIELDS_DEFAULT();
    }
  }

  initPublicWaitFieldsOptions(): void {
    this.publicWaitFieldsOptions = PUBLIC_WAITLIST_FIELD_OPTIONS(
      this.location.displayOptions.labels.customer.replace(/\b\w/u, m => m.toUpperCase()),
      this.location.displayOptions.labels.customer_plural,
    );
    this.publicWaitFieldsOptions.unshift({
      name: 'First Letter of Name Only',
      id: 'first_character',
    });

    this.publicWaitFieldsOptions = this.publicWaitFieldsOptions
      .filter(item => (item.id !== 'note_add' && item.id !== 'note_view'));

    this.imageDisplayOptions = WAILIST_IMAGE_DISPLAY_OPTIONS;
  }

  initDefaultFieldOptions(): void {
    let fieldOptions: PublicWaitlistFieldOptions = {
      ...WAITLIST_FIELD_OPTIONS_DEFAULT,
      ...this.publicWaitlist.fieldOptions,
    };

    this.publicWaitlist.fieldOptions = cloneDeep(fieldOptions);

    if (!this.publicWaitlist.fieldLabels) {
      this.publicWaitlist.fieldLabels = WAITLIST_FIELD_OPTIONS_LABELS_DEFAULT();
    }

    if (!this.publicWaitlist.customText) {
      this.publicWaitlist.customText = WAITLIST_FIELD_OPTIONS_CUSTOM_TEXT_DEFAULT(this.location.name);
    }

    if (!this.publicWaitlist.theme) {
      this.publicWaitlist.theme = WAITLIST_FIELD_SETTINGS_THEME_DEFAULT;
    }

    if (!this.publicWaitlist.fieldSettings) {
      this.publicWaitlist.fieldSettings = { maxSize: WAITLIST_FIELD_SETTING_MAX_TIME_DEFAULT };
    }

    this.publicWaitlistForm = this.service.createPublicWaitlistForm();
    const requiredFields = this.publicWaitlistForm.controls.requiredFields;
    let fieldOptionsForm = this.service.getFieldOptions(requiredFields);

    const selectedTabSub = this.selectedTab.valueChanges.subscribe(() => {
      this.selectedTab.markAsPristine();
    });
    this.subscriptions.add(selectedTabSub);

    const geoFenceEnabledSub = this.geofenceEnabledControl.valueChanges
      .pipe(
        filter((value: boolean) => value && !this.isLocationOnBusinessPlan),
        switchMap(() => this.pricePlansRestrictionsModalService.display(this.authRestaurantService.get())),
        tap(() => {
          this.geofenceEnabledControl.setValue(false);
          this.cdr.detectChanges();
        }),
      ).subscribe();
    this.subscriptions.add(geoFenceEnabledSub);

    this.publicWaitlistForm.addControl('fieldOptions', this.service.createFieldOptionsGroup(fieldOptionsForm));
    this.publicWaitlistForm.patchValue(
      {...this.publicWaitlist, publicUrl: this.location.publicUrl},
      { onlySelf: true }
    );

    this.initModeRadioItems();
    const modeChangeSubscription = this.publicWaitlistForm.get('customText.publicPageSection')
      .valueChanges
      .pipe(tap(() => this.initModeRadioItems()))
      .subscribe();
    this.subscriptions.add(modeChangeSubscription);

    const checkinCapEnabledSubscription = this.checkinCapEnabledControl.valueChanges.subscribe((value) => {
      this.toggleCheckinCapProperties(Boolean(value));
    });
    this.subscriptions.add(checkinCapEnabledSubscription);

    this.checkForPublicPagesEnabled('view');
    this.checkForPublicPagesEnabled('add');
  }

  toggleCheckinCapProperties(value: boolean): void {
    const checkinCapProperties = ['cap', 'reopenDuration', 'message'],
      control = this.publicWaitlistForm.controls['checkinCap'];

    checkinCapProperties.forEach((property) => {
      const prop = control.get(property);
      if (value) {
        prop.enable();
      } else {
        prop.disable();
      }
    });
  }

  applyCheckingCap(): void {
    this.checkinCapState.enabled = this.checkinCapEnabledControl.value;
  }

  checkMessagesContainLink(): boolean {
    // Exclude the appointment reminder and confirmation texts from this check
    let link = Object.keys(this.location.messages).some(key => !['smsReminder', 'smsResConfirmation']
      .includes(key) && this.hasLinks(this.location?.messages[key]));
    if (!link && this.location.selfSignUp.enabled && this.location.selfSignUp.reply.includes('{link}')) {
      link = true;
    }

    return link;
  }

  hasLinks(obj: MessageObj | string): boolean {
    if (typeof obj === 'string') {
      return obj.includes('{link}');
    }

    return obj?.enabled && obj?.message.includes('{link}');
  }

  getSchedule(): void {
    this.subscriptions.add(
      this.scheduleService.selectAll().subscribe(
        (schedule) => {
          this.schedules = schedule || [];
          this.cdr.detectChanges();
        },
        () => {
          this.alertsService.addError('Please configure business hours.');
        },
      ),
    );
  }

  clearOtherScheduleValues(schedules: string[]): void {
    if (schedules.length > 1) {
      this.publicWaitlistForm.get('schedule')?.setValue([schedules[schedules.length - 1]]);
    }
  }

  savePublicWaitlist(): void {
    this.publicWaitlistForm.markAsPristine();
    this.toggleCheckinCapProperties(true);
    this.applyCheckingCap();
    this.whoService.updatePublicWaitlist({
      ...this.publicWaitlistForm.value,
      checkinCapState: this.checkinCapState,
    })
      .subscribe(
        () => {
          this.setPublicPagesList();
          this.publicWaitlistForm.markAsPristine();
          this.alertsService.addSuccess('Public waitlist options');
          this.cdr.detectChanges();
        },
        (err) => {
          this.alertsService.addError(String(err));
        },
      );
  }

  resetToDefaultSettings(): void {
    this.whoService.resetToDefaultPublicSettings()
      .subscribe(
        () => {
          this.changeCurrentSelectedPage();
          this.publicWaitlistForm.markAsPristine();
          this.alertsService.addSuccess('Public waitlist options');
          this.cdr.detectChanges();
        },
        (error) => {
          this.alertsService.addError(String(error));
        },
      );
  }

  uploadFile(): void {
    this.importService.uploadFile(this.file, '/v.1/who/publicWaitlistFileUpload')
      .subscribe(
        (data: PublicWaitlistFileUploadResponse) => {
          this.alertsService.addSuccess('Public waitlist image uploaded successfully');
          this.publicWaitlistForm.patchValue({ image: data.fileUploadedURL });
          this.imageControl.markAsDirty();
          this.cdr.detectChanges();
        },
        (err) => {
          this.alertsService.addError(String(err.error.message));
        },
      );
  }

  onAddFile(files: FileList): void {
    this.file = files[0];
  }

  removeImage(): void {
    const imageUrl = String(this.imageControl?.value);
    this.whoService.removePublicWaitlistImage(imageUrl)
      .subscribe(
        () => {
          this.publicWaitlistForm.markAsPristine();
          this.alertsService.addSuccess('Public waitlist image deleted successfully', 'Deleted:')
          this.publicWaitlistForm.patchValue({ image: '' });
          this.inputVar.nativeElement.value = '';
          this.cdr.detectChanges();
        },
        (error) => {
          this.alertsService.addError(String(error));
        },
      );
  }

  selectPublicPage(): void {
    this.publicWaitlistForm.get('customText.publicPageMode').setValue(this.customTextPagesModes.View);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  trackBy(index: number, item?: OptionItem): number | string {
    return item?.id || index;
  }

  private checkForPublicPagesEnabled(page: string): void {
    const subscription = this.publicWaitlistForm.get(page).valueChanges.subscribe(() => {
      this.setupEmbedLinking();
    });

    this.subscriptions.add(subscription);
  }

  private setupEmbedLinking(): void {
    this.publicCheckinUrl = `${this.origin}/p/check-in/${this.location.publicUrl}`;
    this.publicWaitlistUrl = `${this.origin}/p/waitlist/${this.location.publicUrl}`;
    this.publicOnlineBookingUrl = `${this.origin}/p/book/add/${this.location.publicUrl}`;

    this.setPublicPagesList();
    this.changeCurrentSelectedPage();
    this.cdr.detectChanges();
  }

  // eslint-disable-next-line complexity
  private setPublicPagesList(): void {
    this.publicPages = [];
    const selectedPublicPageControl = this.selectedPublicPageControl;
    const publicOptionsEnabledStates: Record<string, boolean> = {
      view: Boolean(this.viewControl.value),
      add: Boolean(this.addControl.value),
      onlineReservationsEnabled: this.isOnlineBookingPageAvailable,
    };
    const publicOptionsEnabledStatesEntries = Object.entries(publicOptionsEnabledStates);

    for (const [key, value] of publicOptionsEnabledStatesEntries) {
      if (!value && selectedPublicPageControl.value === key) {
        selectedPublicPageControl.setValue(null);
      }
    }

    for (const [key, value] of publicOptionsEnabledStatesEntries) {
      if (value && !selectedPublicPageControl.value) {
        selectedPublicPageControl.setValue(key);
      }

      if (value) {
        this.publicPages.push({
          name: this.publicPagesTabsData[key].dropdownPageName,
          value: key,
        });
      }
    }
  }

  private loadServicesAndResources(): void {
    // TODO: should be subscribed once
    const servicesAndResourcesSub = combineLatest([
      this.servicesService.selectAllEnabled(),
      this.resourcesService.selectAllEnabled(),
    ]).pipe(tap(([services, resources]) => {
      this.services = services;
      this.resources = resources;
      this.setAdditionalFields();
    }))
      .subscribe();

    this.subscriptions.add(servicesAndResourcesSub);
  }

  private setAdditionalFields(): void {
    const fields = [...getFieldsList()];

    if (this.isServicesEnabled) {
      fields.push({
        name: 'Services',
        id: 'services',
        sharedName: 'services',
      });
    }

    if (this.isResourcesEnabled) {
      fields.push({
        name: 'Resources',
        id: 'resources',
        sharedName: 'resources',

      });
    }

    this.manageFieldsList$.next(fields);
  }

  get publicPagesTabsData(): StringKeyDictionary<PublicPageTab> {
    const {
      publicWaitlistUrl,
      publicCheckinUrl,
      publicOnlineBookingUrl,
      reservationLabel,
      reservationPluralLabel,
      reservationButtonText,
    } = this;
    return PUBLIC_PAGES_TABS_DATA({
      publicWaitlistUrl,
      publicCheckinUrl,
      publicOnlineBookingUrl,
      reservationLabel,
      reservationPluralLabel,
      reservationButtonText,
    });
  }

  private initModeRadioItems() {
    this.modeRadioItems = [
      {
        name: 'View',
        value: PublicPagesModes.View,
        disabled: this.isFooterSelected || this.isClosedSelected,
      },
      {
        name: 'Add',
        value: PublicPagesModes.Add,
        disabled: this.isFooterSelected || this.isClosedSelected,
      },
    ];
    this.cdr.detectChanges();
  }
}
